// @ts-nocheck — Phase 1A: legacy code, types added incrementally
import React from "react";
// Phase 3a — migrated off window.toast onto the new Toast primitive.
// The old mount-order workaround (setTimeout fallback) is no longer
// needed because the new toast module is always loaded before any render.
import { toast } from "../components/ui/Toast";
/* global React */
// hooks.jsx — pure utility hooks + helpers extracted from app.jsx as Phase B sub-PR 2.
//
// Why a separate file: app.jsx was 5,297 LOC mixing 12 concerns. ROLLOUT.md §4
// breaks that monolith into 5 modules; this is sub-PR 2 (after sub-PR 1
// state-storage). Pure utilities + a11y hooks live here because they have:
//   - no React state of their own (only useEffect side effects)
//   - no business logic
//   - no UI rendering
//   - no dependency on app/dashboard state
//
// Loading: imported via <script type="text/babel" src="hooks.jsx"> in
// Forecast.html, BEFORE app.jsx. Each helper publishes itself onto window so
// app.jsx can destructure: `const { deepClone, useEscapeClose, ... } = window;`
//
// Migration plan: when Babel-in-browser is replaced with a build step (PERF-1,
// deferred to Phase F), hooks.jsx becomes a real ES module with `export`
// statements and `tests/hooks.test.mjs` imports them directly instead of
// mirroring (the `migrations.test.mjs` and `schemas.test.mjs` patterns become
// import-based too). For now, every helper here is also mirrored in
// `tests/migrations.test.mjs` (deepClone) and `tests/schemas.test.mjs`
// (validateOrFallback's schema dependency); when changing a helper, mirror.

// ─── Shared a11y / interaction hooks ────────────────────────────────────────
// Single Escape-to-close hook used by every dismissable surface (modals, dropdowns,
// floating panels). Without this, each surface ships its own keydown listener — easy
// to miss, hard to audit. Pass `open` so closed surfaces don't pollute the keydown
// channel; pass `onClose` and we wire it up.
function useEscapeClose(open, onClose) {
  React.useEffect(() => {
    if (!open || typeof onClose !== "function") return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

// Click-outside-to-close. Pass a ref to the surface; when `enabled` and the user
// clicks outside it, `onClose` fires. mousedown (not click) so it fires before
// any interactive element inside the click target swallows it.
function useOutsideClick(ref, onClose, enabled = true) {
  React.useEffect(() => {
    if (!enabled || typeof onClose !== "function") return;
    const onDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [ref, onClose, enabled]);
}

// Focus trap for modals/dialogs. While `active`, Tab and Shift+Tab cycle inside
// the container. On unmount or when `active` flips false, focus restores to whatever
// element was focused before the trap activated. WCAG 2.4.3 keyboard navigation.
function useFocusTrap(containerRef, active) {
  React.useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const previouslyFocused = document.activeElement;

    // Move focus to first focusable element on activation. We pick the first
    // focusable inside, falling back to the container itself (with tabIndex=-1).
    const focusableSel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSel))
      .filter(el => el.offsetParent !== null || el === document.activeElement);

    const initial = getFocusable()[0];
    if (initial && !container.contains(document.activeElement)) initial.focus();

    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const els = getFocusable();
      if (els.length === 0) { e.preventDefault(); return; }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("keydown", onKey);
      // Restore focus to where the user was before the trap activated.
      try { previouslyFocused?.focus?.(); } catch {}
    };
  }, [active, containerRef]);
}

// ─── Pure data helpers ──────────────────────────────────────────────────────

// Defensive deep-clone for state hydration (R3 from CODE_AUDIT.md).
// State trees loaded from localStorage or KV share nested-object references with the
// parsed payload. If any code path holds a ref to that payload (e.g. a captured
// closure during the hydrate effect) and mutates a nested field, mutations bleed
// into the React state. Wrapping with structuredClone severs the connection.
//
// Uses native structuredClone where available (Chrome 98+, Firefox 94+, Safari 15.4+);
// falls back to JSON parse/stringify for older runtimes. Both produce a fresh tree.
//
// Tested: tests/migrations.test.mjs::deepClone (6 tests).
function deepClone(obj) {
  if (obj == null) return obj;
  if (typeof structuredClone === "function") {
    try { return structuredClone(obj); } catch { /* circular/unserializable — fall through */ }
  }
  return JSON.parse(JSON.stringify(obj));
}

// Date-from-ISO with NaN guard. `new Date("")` and `new Date(null)` both produce
// Invalid Date objects whose getTime() returns NaN — silently breaking comparisons,
// filters, and "days since" math. This helper returns null on bad input so call
// sites can branch deliberately. Use everywhere we parse HubSpot date strings.
function safeDate(iso) {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}
function safeDateMs(iso) {
  const d = safeDate(iso);
  return d ? d.getTime() : null;
}

// ─── Boundary validation (Phase A) ─────────────────────────────────────────
// Wrap a Zod safeParse with: log on failure, throttled toast, fallback return.
// Used at the 3 boundaries where untrusted JSON enters the app:
//   1. localStorage state load   (corrupted via DevTools / browser bug / quota loss)
//   2. KV /api/state load         (drift between worker schema and frontend schema)
//   3. HubSpot /api/actuals load  (worker bug / CF outage / mid-deploy partial response)
//
// Toast is throttled per-scope (one warning per minute per boundary) so a
// repeatedly-failing fetch doesn't spam. Returns the validated payload on
// success, fallback on failure. If `schema` is falsy (Zod CDN didn't load),
// returns the raw payload — the dashboard prefers degrading-but-running over
// blocking.
const validateToastSeenRef = { current: {} };
function validateOrFallback(schema, payload, fallback, scope, lang) {
  if (!schema || typeof schema.safeParse !== "function") return payload; // Zod not loaded
  const result = schema.safeParse(payload);
  if (result.success) return result.data;
  // Log first 3 issues — full Zod error trees are noisy; the first few usually
  // pinpoint the problem. Tag with scope so multi-boundary failures are scannable.
  const issues = (result.error && result.error.issues) || [];
  console.warn(`[validate:${scope}] ${issues.length} issue(s):`,
    issues.slice(0, 3).map(i => `${i.path?.join(".") || "(root)"}: ${i.message}`));
  const now = Date.now();
  // Phase 3a — throttle: at most one toast per scope per minute. The new
  // toast primitive is module-scoped and always loaded, so the mount-order
  // setTimeout workaround the previous implementation needed is gone.
  if (typeof window === "undefined" || (now - (validateToastSeenRef.current[scope] || 0)) <= 60000) {
    return fallback;
  }
  validateToastSeenRef.current[scope] = now;
  const isIt = (lang || "it") === "it";
  const titles = {
    localStorage: isIt ? "Stato locale corrotto" : "Local state corrupt",
    kvState: isIt ? "Stato remoto malformato" : "Remote state malformed",
    hubspotActuals: isIt ? "Dati HubSpot malformati" : "HubSpot data malformed",
  };
  const bodies = {
    localStorage: isIt ? "Caricato lo stato di default. Le tue modifiche locali sono salvate altrove se hai una password di edit." : "Loaded default state. Your local edits are saved elsewhere if you have an edit password.",
    kvState: isIt ? "Stato remoto ignorato — usata la copia locale. Riprova tra qualche secondo." : "Remote state ignored — using local copy. Retry in a few seconds.",
    hubspotActuals: isIt ? "Dati HubSpot ignorati. La dashboard mostra l'ultima versione valida." : "HubSpot data ignored. Dashboard shows the last valid version.",
  };
  toast.warn({
    title: titles[scope] || "Validation",
    message: bodies[scope] || "Payload validation failed.",
  });
  return fallback;
}

// ─── Publish to window for app.jsx ─────────────────────────────────────────
// Each <script type="text/babel"> is its own scope under Babel-standalone — there
// are no real ES imports. Mirroring the model.jsx pattern: assign to window
// directly so app.jsx can destructure at the top: `const { deepClone, ... } = window;`
// HOOKS namespace is provided too for explicit reference (e.g. window.HOOKS.deepClone).
Object.assign(window, {
  useEscapeClose,
  useOutsideClick,
  useFocusTrap,
  deepClone,
  safeDate,
  safeDateMs,
  validateOrFallback,
});
window.HOOKS = {
  useEscapeClose,
  useOutsideClick,
  useFocusTrap,
  deepClone,
  safeDate,
  safeDateMs,
  validateOrFallback,
};
